/**
 * When the user switches the active playlist (provider), the previously-
 * playing stream is no longer valid — its id usually doesn't exist on the
 * new panel, so the player would surface a "not found" error. Tear down
 * the player, clear the mini-player, and bounce off /watch so the user
 * lands on the new provider's catalog instead.
 *
 * Skipped on the initial mount where `activePlaylist` hydrates from
 * localStorage — that transition is the app starting up, not a user
 * switch.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCatalog } from "../contexts/CatalogContext";
import { usePlayback } from "../contexts/PlaybackContext";
import { destroyPlayer } from "../components/Player";

export function usePlaylistSwitchReset() {
  const { activePlaylist } = useCatalog();
  const { clear } = usePlayback();
  const navigate = useNavigate();
  const location = useLocation();
  const prevId = useRef<number | null>(null);
  const initialized = useRef(false);

  useEffect(() => {
    const currentId = activePlaylist?.id ?? null;
    if (!initialized.current) {
      prevId.current = currentId;
      if (currentId !== null) initialized.current = true;
      return;
    }
    if (currentId !== prevId.current) {
      destroyPlayer();
      clear();
      if (location.pathname.startsWith("/watch")) {
        // Stream id from the previous provider is gone — bounce to home.
        navigate("/livetv", { replace: true });
      } else if (location.search) {
        // Clear URL filters/queries (?cat=X, ?q=Y, etc.) — those category
        // ids and search terms belong to the previous playlist and would
        // produce an empty grid on the new one.
        navigate(location.pathname, { replace: true });
      }
      prevId.current = currentId;
    }
  }, [activePlaylist?.id, clear, navigate, location.pathname]);
}
